import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { formatCurrency, formatNumber } from '../../utils/formatters.js';

/**
 * Create a credit note from a posted/paid sales invoice. `invoice` must carry
 * its `lines`. Each line can be credited up to its invoiced quantity.
 */
export function CreditNoteCreateModal({ invoice, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [qty, setQty] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    setQty({});
    setError(null);
  }, [invoice]);

  const create = useMutation({
    mutationFn: (body) => financeApi.createCreditNote(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance', 'invoices'] });
      queryClient.invalidateQueries({ queryKey: ['finance', 'credit-notes'] });
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  const lines = invoice?.lines || [];
  const total = lines.reduce((acc, l) => acc + Number(qty[l.id] || 0) * Number(l.unitPrice), 0);

  function submit(e) {
    e.preventDefault();
    setError(null);
    const body = {
      invoiceId: invoice.id,
      creditDate: new Date().toISOString().slice(0, 10),
      lines: lines
        .map((l) => ({ invoiceLineId: l.id, quantity: Number(qty[l.id] || 0) }))
        .filter((l) => l.quantity > 0),
    };
    if (body.lines.length === 0) {
      setError(t('creditNote.create.pickLine'));
      return;
    }
    create.mutate(body);
  }

  return (
    <Modal
      open={Boolean(invoice)}
      onClose={onClose}
      title={t('creditNote.create.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={submit} disabled={create.isPending}>
            {create.isPending ? `${t('common.save')}…` : t('creditNote.create.submit')}
          </Button>
        </>
      }
    >
      {invoice && (
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <p className="muted text-sm" style={{ margin: 0 }}>
            <strong>{invoice.invoiceNumber}</strong> - {t('creditNote.create.intro')}
          </p>
          {lines.map((l) => (
            <div key={l.id} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', padding: 'var(--space-3)', border: '1px solid var(--color-hairline)', borderRadius: 'var(--radius-md)' }}>
              <strong>{l.description}</strong>
              <div className="muted text-xs" style={{ display: 'flex', gap: 'var(--space-3)' }}>
                <span>{t('detail.lineQty')}: <span className="mono">{formatNumber(l.quantity)}</span></span>
                <span>{t('detail.lineUnitPrice')}: <span className="mono">{formatCurrency(l.unitPrice, invoice.currency)}</span></span>
              </div>
              <label className="field">
                <span className="field-label">{t('creditNote.create.creditQty')}</span>
                <input
                  className="input"
                  type="number"
                  min="0"
                  max={l.quantity}
                  step="any"
                  value={qty[l.id] ?? ''}
                  onChange={(e) => setQty((p) => ({ ...p, [l.id]: e.target.value }))}
                />
              </label>
            </div>
          ))}
          <div style={{ textAlign: 'right' }}>
            <strong>{t('creditNote.print.total')}: {formatCurrency(total, invoice.currency)}</strong>
          </div>
          {error && <div className="alert danger">{error}</div>}
        </form>
      )}
    </Modal>
  );
}
